import type { TabId } from './types'

interface GroupedTabBarProps {
  activeTab: TabId
  onTabChange: (tab: TabId) => void
}

interface PillProps {
  id: TabId
  label: string
  activeTab: TabId
  onTabChange: (tab: TabId) => void
}

function Pill({ id, label, activeTab, onTabChange }: PillProps) {
  const isActive = activeTab === id
  return (
    <button
      onClick={() => onTabChange(id)}
      className="px-3 py-[6px] rounded-[8px] text-[12px] font-semibold transition-all whitespace-nowrap"
      style={isActive
        ? {
          background: '#6366f1',
          border: '1px solid #6366f1',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(99,102,241,.33)',
        }
        : {
          background: '#0f1a2e',
          border: '1px solid #233149',
          color: '#94a3b8',
        }
      }
    >
      {label}
    </button>
  )
}

export function GroupedTabBar({ activeTab, onTabChange }: GroupedTabBarProps) {
  const groupLabelCls = 'text-[9px] font-bold uppercase tracking-[.13em] text-[#3f4f6b] mb-1.5 block'
  const dividerCls = 'w-px self-stretch bg-[#16223a] mx-2'

  return (
    <div
      className="flex items-end overflow-x-auto"
      style={{
        padding: '12px 20px 0',
        borderBottom: '1px solid #1e2c44',
        gap: 0,
      }}
    >
      {/* KYC group */}
      <div className="flex flex-col pb-3 pr-4">
        <span className={groupLabelCls}>KYC</span>
        <div className="flex items-center gap-1.5">
          <Pill id="kyc-v2" label="v2" activeTab={activeTab} onTabChange={onTabChange} />
          <Pill id="kyc-v1" label="v1" activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>
      <div className={dividerCls} />

      {/* KYB group */}
      <div className="flex flex-col pb-3 px-4">
        <span className={groupLabelCls}>KYB</span>
        <div className="flex items-center gap-1.5">
          <Pill id="kyb-v2" label="v2" activeTab={activeTab} onTabChange={onTabChange} />
          <Pill id="kyb-v1" label="v1" activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>
      <div className={dividerCls} />

      {/* Hosted group */}
      <div className="flex flex-col pb-3 px-4">
        <span className={groupLabelCls}>Hosted</span>
        <div className="flex items-center gap-1.5">
          <Pill id="hosted-v2" label="v2" activeTab={activeTab} onTabChange={onTabChange} />
          <Pill id="hosted-v1" label="v1" activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>
      <div className={dividerCls} />

      {/* Embedded group */}
      <div className="flex flex-col pb-3 px-4">
        <span className={groupLabelCls}>Embedded</span>
        <div className="flex items-center gap-1.5">
          <Pill id="embedded" label="OneSDK" activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>
      <div className={dividerCls} />

      {/* Webhooks group */}
      <div className="flex flex-col pb-3 px-4">
        <span className={groupLabelCls}>Webhooks</span>
        <div className="flex items-center gap-1.5">
          <Pill id="webhooks" label="Receiver" activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>
      <div className={dividerCls} />

      {/* Explorer group */}
      <div className="flex flex-col pb-3 px-4">
        <span className={groupLabelCls}>Explorer</span>
        <div className="flex items-center gap-1.5">
          <Pill id="explorer" label="API" activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Secondary tabs — right-aligned */}
      <div className="flex items-end gap-1 pb-3">
        {(['uat', 'test-data', 'guide'] as const).map(id => {
          const labels: Record<string, string> = { uat: 'UAT Generator', 'test-data': 'Test Data', guide: 'Guide' }
          const isActive = activeTab === id
          return (
            <button
              key={id}
              onClick={() => onTabChange(id)}
              className="px-3 py-[6px] text-[12px] font-medium rounded-[8px] transition-colors whitespace-nowrap"
              style={isActive
                ? { background: '#111c30', border: '1px solid #2a3a57', color: '#f1f5f9' }
                : { background: 'transparent', border: '1px solid transparent', color: '#7e8ba6' }
              }
            >
              {labels[id]}
            </button>
          )
        })}
      </div>
    </div>
  )
}
